import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import MainLayout from '../../components/layout/MainLayout';
import ObituaryForm from '../../components/obituary/ObituaryForm';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import { useToast } from '../../context/ToastContext';

/**
 * ObituaryCreatePC - PC 전용 부고 작성 페이지 (단일 페이지)
 * 모든 정보를 한 페이지에 입력하는 PC 최적화 버전
 */
const ObituaryCreatePC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  // localStorage에서 기존 데이터 불러오기
  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem('obituaryDraftPC');
    return saved ? JSON.parse(saved) : {
      // 고인 정보
      deceasedName: '',
      deceasedBirthDate: '',
      deceasedDeathDate: '',
      relationship: '',
      // 장례 정보
      funeralHome: '',
      funeralAddress: '',
      funeralRoom: '',
      departureDate: '',
      departureTime: '',
      // 상주 정보
      chiefMourner: '',
      chiefMournerPhone: '',
      chiefMournerRelationship: '',
      additionalMourners: []
    };
  });

  const [errors, setErrors] = useState({});
  const [showPreview, setShowPreview] = useState(false);

  // 폼 데이터 변경 핸들러
  const handleChange = (newData) => {
    setFormData(newData);
    // localStorage에 임시 저장
    localStorage.setItem('obituaryDraftPC', JSON.stringify(newData));
    // 해당 필드 에러 초기화
    const changedField = Object.keys(newData).find(key => newData[key] !== formData[key]);
    if (changedField && errors[changedField]) {
      const newErrors = { ...errors };
      delete newErrors[changedField];
      setErrors(newErrors);
    }
  };

  // 유효성 검사
  const validateForm = () => {
    const newErrors = {};

    // 고인 정보
    if (!formData.deceasedName?.trim()) {
      newErrors.deceasedName = '고인명을 입력해주세요.';
    }
    if (!formData.deceasedBirthDate) {
      newErrors.deceasedBirthDate = '생년월일을 입력해주세요.';
    }
    if (!formData.deceasedDeathDate) {
      newErrors.deceasedDeathDate = '사망일을 입력해주세요.';
    }
    if (!formData.relationship) {
      newErrors.relationship = '고인과의 관계를 선택해주세요.';
    }

    // 생년월일 < 사망일 검증
    if (formData.deceasedBirthDate && formData.deceasedDeathDate) {
      if (new Date(formData.deceasedBirthDate) > new Date(formData.deceasedDeathDate)) {
        newErrors.deceasedDeathDate = '사망일은 생년월일 이후여야 합니다.';
      }
    }

    // 장례 정보
    if (!formData.funeralHome?.trim()) {
      newErrors.funeralHome = '장례식장명을 입력해주세요.';
    }
    if (!formData.funeralAddress?.trim()) {
      newErrors.funeralAddress = '장례식장 주소를 입력해주세요.';
    }
    if (!formData.funeralRoom?.trim()) {
      newErrors.funeralRoom = '호실을 입력해주세요.';
    }
    if (!formData.departureDate) {
      newErrors.departureDate = '발인일을 입력해주세요.';
    }
    if (!formData.departureTime) {
      newErrors.departureTime = '발인시간을 입력해주세요.';
    }

    // 발인일 > 사망일 검증
    if (formData.deceasedDeathDate && formData.departureDate) {
      if (new Date(formData.departureDate) < new Date(formData.deceasedDeathDate)) {
        newErrors.departureDate = '발인일은 사망일 이후여야 합니다.';
      }
    }

    // 상주 정보
    if (!formData.chiefMourner?.trim()) {
      newErrors.chiefMourner = '상주명을 입력해주세요.';
    }
    if (!formData.chiefMournerPhone?.trim()) {
      newErrors.chiefMournerPhone = '상주 연락처를 입력해주세요.';
    } else {
      const phonePattern = /^01[0-9]-?\d{3,4}-?\d{4}$/;
      if (!phonePattern.test(formData.chiefMournerPhone.replace(/-/g, ''))) {
        newErrors.chiefMournerPhone = '올바른 전화번호 형식이 아닙니다.';
      }
    }
    if (!formData.chiefMournerRelationship?.trim()) {
      newErrors.chiefMournerRelationship = '상주 관계를 입력해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 취소
  const handleCancel = () => {
    if (window.confirm('작성 중인 내용이 저장됩니다. 취소하시겠습니까?')) {
      navigate('/');
    }
  };

  // 미리보기
  const handlePreview = () => {
    if (validateForm()) {
      setShowPreview(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      showToast('필수 항목을 모두 입력해주세요.', 'error');
    }
  };

  // 제출
  const handleSubmit = async () => {
    if (!validateForm()) {
      setShowPreview(false);
      showToast('필수 항목을 모두 입력해주세요.', 'error');
      return;
    }

    try {
      // Mock API 호출
      const newObituary = {
        ...formData,
        id: Date.now(),
        userId: 1,
        isFavorite: false,
        createdAt: new Date().toISOString(),
        lat: 37.5800,
        lng: 127.0020
      };

      console.log('Creating obituary:', newObituary);

      // localStorage 초기화
      localStorage.removeItem('obituaryDraftPC');

      showToast('부고가 성공적으로 등록되었습니다.', 'success');

      setTimeout(() => {
        navigate('/obituaries');
      }, 1000);
    } catch (error) {
      console.error('Failed to create obituary:', error);
      showToast('부고 등록에 실패했습니다.', 'error');
    }
  };

  // 미리보기 렌더링
  const renderPreview = () => {
    if (!formData) return null;

    const formattedDepartureDate = formData.departureDate
      ? format(new Date(formData.departureDate), 'yyyy년 MM월 dd일 (E)', { locale: ko })
      : '';

    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-primary-900 mb-2">부고</h2>
          <div className="w-16 h-1 bg-primary-500 mx-auto mb-6" />
        </div>

        <Card>
          <div className="space-y-4">
            {/* 고인 정보 */}
            <div className="text-center border-b pb-4">
              <h3 className="text-xl font-bold text-primary-900 mb-2">
                故 {formData.deceasedName}
              </h3>
              <p className="text-sm text-gray-600">
                {formData.chiefMourner} {formData.chiefMournerRelationship}의 {formData.relationship}
              </p>
            </div>

            {/* 장례식장 정보 */}
            <div className="space-y-2 text-sm">
              <div className="flex">
                <span className="w-24 font-semibold text-gray-700">빈소</span>
                <span className="text-gray-900">{formData.funeralHome} {formData.funeralRoom}</span>
              </div>
              <div className="flex">
                <span className="w-24 font-semibold text-gray-700">주소</span>
                <span className="text-gray-900">{formData.funeralAddress}</span>
              </div>
              <div className="flex">
                <span className="w-24 font-semibold text-gray-700">발인</span>
                <span className="text-gray-900">{formattedDepartureDate} {formData.departureTime}</span>
              </div>
            </div>

            {/* 상주 정보 */}
            <div className="border-t pt-4 space-y-2 text-sm">
              <div className="flex">
                <span className="w-24 font-semibold text-gray-700">상주</span>
                <span className="text-gray-900">{formData.chiefMourner} ({formData.chiefMournerRelationship})</span>
              </div>
              <div className="flex">
                <span className="w-24 font-semibold text-gray-700">연락처</span>
                <span className="text-gray-900">{formData.chiefMournerPhone}</span>
              </div>

              {/* 추가 상주 */}
              {formData.additionalMourners && formData.additionalMourners.length > 0 && (
                <div className="flex">
                  <span className="w-24 font-semibold text-gray-700">추가상주</span>
                  <div className="space-y-1">
                    {formData.additionalMourners.map((mourner, index) => (
                      <div key={index} className="text-gray-900">
                        {mourner.name} ({mourner.relationship})
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* 미리보기 액션 */}
        <div className="flex gap-3 justify-center max-w-md mx-auto">
          <Button
            variant="outline"
            onClick={() => setShowPreview(false)}
            className="flex-1"
          >
            수정
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            className="flex-1"
          >
            등록
          </Button>
        </div>
      </div>
    );
  };

  // 미리보기 모드
  if (showPreview) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-primary-900 mb-2">
              미리보기
            </h1>
            <p className="text-gray-600">
              부고 내용을 확인하고 등록해주세요.
            </p>
          </div>

          {renderPreview()}
        </div>
      </MainLayout>
    );
  }

  // 폼 입력 모드
  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-primary-900 mb-2">
            부고 작성 (PC)
          </h1>
          <p className="text-gray-600">
            모든 정보를 입력하고 미리보기로 확인해주세요.
          </p>
        </div>

        {/* Form Sections in Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* 섹션 1: 고인 정보 */}
          <Card className="lg:col-span-1">
            <h2 className="text-xl font-bold text-primary-900 mb-4 pb-3 border-b">
              1. 고인 정보
            </h2>
            <ObituaryForm
              formData={formData}
              onChange={handleChange}
              errors={errors}
              step="deceased"
            />
          </Card>

          {/* 섹션 2: 장례 정보 */}
          <Card className="lg:col-span-1">
            <h2 className="text-xl font-bold text-primary-900 mb-4 pb-3 border-b">
              2. 장례 정보
            </h2>
            <ObituaryForm
              formData={formData}
              onChange={handleChange}
              errors={errors}
              step="funeral"
            />
          </Card>

          {/* 섹션 3: 상주 정보 */}
          <Card className="lg:col-span-1">
            <h2 className="text-xl font-bold text-primary-900 mb-4 pb-3 border-b">
              3. 상주 정보
            </h2>
            <ObituaryForm
              formData={formData}
              onChange={handleChange}
              errors={errors}
              step="mourner"
            />
          </Card>
        </div>

        {/* Actions */}
        <div className="flex gap-4 justify-center max-w-2xl mx-auto">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="px-8"
          >
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handlePreview}
            className="px-8"
          >
            미리보기
          </Button>
        </div>

        {/* Info */}
        <div className="mt-8 p-6 bg-accent-50 rounded-lg border border-accent-200 max-w-2xl mx-auto">
          <p className="text-sm text-gray-700 text-center">
            <span className="font-semibold">안내:</span> 작성 중인 내용은 자동으로 임시 저장됩니다.
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default ObituaryCreatePC;
